// Production-Ready Mind Map Viewer
// Features: Canvas panning, fullscreen, multiple export formats, node interactions

import { useState, useRef, useCallback, useMemo } from 'react';
import { 
  ZoomIn, 
  ZoomOut, 
  Maximize2, 
  Download, 
  Share2,
  Minus,
  Plus,
  Move,
  List,
  Network,
  Copy,
  FileImage,
  FileText,
  Image,
} from 'lucide-react';
import { toPng, toSvg } from 'html-to-image';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { MindMap, MindMapNode } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';

interface MindMapViewerProps {
  mindMap: MindMap;
  className?: string;
}

// Enhanced color palette with better contrast
const NODE_COLORS = [
  { bg: 'bg-blue-100 dark:bg-blue-900/40', border: 'border-blue-400 dark:border-blue-600', text: 'text-blue-800 dark:text-blue-200', line: '#60a5fa' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/40', border: 'border-emerald-400 dark:border-emerald-600', text: 'text-emerald-800 dark:text-emerald-200', line: '#34d399' },
  { bg: 'bg-purple-100 dark:bg-purple-900/40', border: 'border-purple-400 dark:border-purple-600', text: 'text-purple-800 dark:text-purple-200', line: '#a78bfa' },
  { bg: 'bg-amber-100 dark:bg-amber-900/40', border: 'border-amber-400 dark:border-amber-600', text: 'text-amber-800 dark:text-amber-200', line: '#fbbf24' },
  { bg: 'bg-rose-100 dark:bg-rose-900/40', border: 'border-rose-400 dark:border-rose-600', text: 'text-rose-800 dark:text-rose-200', line: '#fb7185' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/40', border: 'border-cyan-400 dark:border-cyan-600', text: 'text-cyan-800 dark:text-cyan-200', line: '#22d3ee' },
];

// Recursive node component with enhanced interactions
function MindMapNodeComponent({
  node,
  level,
  colorIndex,
  isExpanded,
  onToggle,
  selectedNode,
  onSelect,
}: {
  node: MindMapNode;
  level: number;
  colorIndex: number;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  selectedNode: string | null;
  onSelect: (id: string) => void;
}) {
  const colors = NODE_COLORS[colorIndex % NODE_COLORS.length];
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedNode === node.id;

  return (
    <div className="flex flex-col items-start">
      <div
        className={cn(
          'px-4 py-2 rounded-lg border-2 cursor-pointer transition-all',
          colors.bg,
          colors.border,
          colors.text,
          'hover:shadow-md hover:scale-[1.02]',
          level === 0 && 'text-lg font-bold px-6 py-3',
          isSelected && 'ring-2 ring-primary ring-offset-2'
        )}
        onClick={() => {
          onSelect(node.id);
          if (hasChildren) onToggle(node.id);
        }}
      >
        <span className="font-medium">{node.label}</span>
        {hasChildren && (
          <span className="ml-2 text-xs opacity-60">
            {isExpanded ? '−' : '+'} {node.children!.length}
          </span>
        )}
      </div>
      {node.description && (
        <p className="text-sm text-muted-foreground mt-1.5 ml-2 max-w-sm leading-relaxed">
          {node.description}
        </p>
      )}
      {hasChildren && isExpanded && (
        <div 
          className="ml-6 mt-3 pl-4 border-l-2 border-dashed space-y-1"
          style={{ borderColor: colors.line }}
        >
          {node.children!.map((child, idx) => (
            <div key={child.id} className="mb-2">
              <MindMapNodeComponent
                node={child}
                level={level + 1}
                colorIndex={colorIndex + idx + 1}
                isExpanded={isExpanded}
                onToggle={onToggle}
                selectedNode={selectedNode}
                onSelect={onSelect}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Canvas-based radial layout
function RadialMindMap({
  mindMap,
  zoom,
  pan,
  onPan,
  selectedNode,
  onSelectNode,
  contentRef,
}: {
  mindMap: MindMap;
  zoom: number;
  pan: { x: number; y: number };
  onPan: (delta: { x: number; y: number }) => void;
  selectedNode: string | null;
  onSelectNode: (id: string) => void;
  contentRef?: React.RefObject<HTMLDivElement>;
}) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    new Set(mindMap.nodes.map((n) => n.id))
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const toggleNode = (id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      onPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Count total nodes
  const totalNodes = useMemo(() => {
    let count = 0;
    const countNodes = (nodes: MindMapNode[]) => {
      nodes.forEach(n => {
        count++;
        if (n.children) countNodes(n.children);
      });
    };
    countNodes(mindMap.nodes);
    return count;
  }, [mindMap.nodes]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative min-h-[600px] overflow-hidden rounded-xl border bg-background",
        isDragging ? 'cursor-grabbing' : 'cursor-grab'
      )}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        ref={contentRef}
        className="p-8 transition-transform duration-100"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
        }}
      >
        {/* Central topic */}
        <div className="flex flex-col items-center mb-8">
          <div 
            className={cn(
              "px-8 py-5 bg-primary text-primary-foreground rounded-2xl text-2xl font-bold shadow-lg",
              "transform transition-all hover:scale-105"
            )}
          >
            {mindMap.centralTopic}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="secondary" className="text-xs">
              {totalNodes} topics
            </Badge>
          </div>
          <div className="w-0.5 h-10 bg-gradient-to-b from-primary to-muted-foreground/30 mt-2" />
        </div>

        {/* Main branches - radial layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {mindMap.nodes.map((node, index) => (
            <div key={node.id} className="relative">
              <MindMapNodeComponent
                node={node}
                level={0}
                colorIndex={index}
                isExpanded={expandedNodes.has(node.id)}
                onToggle={toggleNode}
                selectedNode={selectedNode}
                onSelect={onSelectNode}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Outline/list view with search
function ListMindMap({ 
  mindMap,
  selectedNode,
  onSelectNode,
}: { 
  mindMap: MindMap;
  selectedNode: string | null;
  onSelectNode: (id: string) => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const renderNode = (node: MindMapNode, level: number = 0): React.ReactNode => {
    const matchesSearch = searchTerm 
      ? node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.description?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    if (!matchesSearch && !node.children?.some(c => 
      c.label.toLowerCase().includes(searchTerm.toLowerCase())
    )) {
      return null;
    }

    const colors = NODE_COLORS[level % NODE_COLORS.length];
    const isSelected = selectedNode === node.id;

    return (
      <div key={node.id} className={cn('py-1', level > 0 && 'ml-4')}>
        <div 
          className={cn(
            "flex items-start gap-2 p-2 rounded-lg transition-colors cursor-pointer",
            "hover:bg-muted/50",
            isSelected && "bg-primary/10 ring-1 ring-primary"
          )}
          onClick={() => onSelectNode(node.id)}
        >
          <span className={cn(
            "flex-shrink-0 w-2 h-2 rounded-full mt-2",
            colors.bg,
            colors.border,
            "border"
          )} />
          <div className="flex-1 min-w-0">
            <span className={cn(level === 0 && 'font-semibold')}>
              {node.label}
            </span>
            {node.description && (
              <p className="text-sm text-muted-foreground mt-0.5">{node.description}</p>
            )}
          </div>
        </div>
        {node.children?.map((child) => renderNode(child, level + 1))}
      </div>
    );
  };

  return (
    <ScrollArea className="h-[500px]">
      <div className="p-4">
        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search topics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 text-sm border rounded-lg bg-background"
          />
        </div>

        <h3 className="text-lg font-bold mb-4 text-primary">{mindMap.centralTopic}</h3>
        <div className="space-y-1">
          {mindMap.nodes.map((node) => renderNode(node))}
        </div>
      </div>
    </ScrollArea>
  );
}

export function MindMapViewer({ mindMap, className }: MindMapViewerProps) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [viewMode, setViewMode] = useState<'radial' | 'list'>('radial');
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const mindMapContentRef = useRef<HTMLDivElement>(null);
  
  // Export functionality
  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();
  const [isPngExporting, setIsPngExporting] = useState(false);

  const zoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.3));
  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Export as PNG image
  const exportAsPng = useCallback(async () => {
    if (!mindMapContentRef.current) {
      toast.error('Mind map not ready for export');
      return;
    }
    setIsPngExporting(true);
    try {
      const node = mindMapContentRef.current;
      // Reset transform for capture so we get the full content at natural size
      const originalTransform = node.style.transform;
      node.style.transform = 'none';
      
      const dataUrl = await toPng(node, {
        backgroundColor: '#ffffff',
        pixelRatio: 2, // High quality
        quality: 1,
        cacheBust: true,
      });
      
      // Restore transform
      node.style.transform = originalTransform;

      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const cleanTitle = mindMap.title
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
      const filename = `${cleanTitle}_Mind-Map_${dateStr}.png`;
      
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
      toast.success('PNG image downloaded!');
    } catch (error) {
      console.error('PNG export failed:', error);
      toast.error('Failed to export as PNG');
    } finally {
      setIsPngExporting(false);
    }
  }, [mindMap.title]);

  // Export as SVG
  const exportAsSvg = useCallback(async () => {
    if (!mindMapContentRef.current) {
      toast.error('Mind map not ready for export');
      return;
    }
    setIsPngExporting(true);
    try {
      const node = mindMapContentRef.current;
      // Reset transform for capture
      const originalTransform = node.style.transform;
      node.style.transform = 'none';
      
      const dataUrl = await toSvg(node, {
        backgroundColor: '#ffffff',
        cacheBust: true,
      });
      
      // Restore transform
      node.style.transform = originalTransform;

      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const cleanTitle = mindMap.title
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
      const filename = `${cleanTitle}_Mind-Map_${dateStr}.svg`;
      
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
      toast.success('SVG image downloaded!');
    } catch (error) {
      console.error('SVG export failed:', error);
      toast.error('Failed to export as SVG');
    } finally {
      setIsPngExporting(false);
    }
  }, [mindMap.title]);

  // Export as Markdown
  const exportAsMarkdown = () => {
    const text = `# ${mindMap.title}\n\n## ${mindMap.centralTopic}\n\n${mindMap.nodes
      .map((n) => {
        let result = `### ${n.label}\n`;
        if (n.description) result += `${n.description}\n`;
        if (n.children) {
          result += n.children.map((c) => `- **${c.label}**${c.description ? `: ${c.description}` : ''}`).join('\n');
        }
        return result;
      })
      .join('\n\n')}`;

    const blob = new Blob([text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mindMap.title.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as Markdown!');
  };

  // Export as JSON
  const exportAsJSON = () => {
    const blob = new Blob([JSON.stringify(mindMap, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mindMap.title.replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON!');
  };

  // Copy as text
  const copyAsText = async () => {
    const formatNode = (node: MindMapNode, indent: string = ''): string => {
      let text = `${indent}• ${node.label}`;
      if (node.description) text += ` - ${node.description}`;
      text += '\n';
      if (node.children) {
        text += node.children.map(c => formatNode(c, indent + '  ')).join('');
      }
      return text;
    };

    const text = `${mindMap.centralTopic}\n\n${mindMap.nodes.map(n => formatNode(n)).join('\n')}`;
    
    await navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Get selected node details
  const selectedNodeData = useMemo(() => {
    if (!selectedNode) return null;
    
    const findNode = (nodes: MindMapNode[]): MindMapNode | null => {
      for (const node of nodes) {
        if (node.id === selectedNode) return node;
        if (node.children) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    return findNode(mindMap.nodes);
  }, [selectedNode, mindMap.nodes]);

  return (
    <TooltipProvider>
      <div ref={containerRef} className={cn('flex flex-col', className)}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">{mindMap.title}</h3>
            <p className="text-sm text-muted-foreground">
              {mindMap.nodes.length} main topics
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Zoom controls */}
            <div className="flex items-center border rounded-lg bg-background">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={zoomOut}>
                    <Minus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom out</TooltipContent>
              </Tooltip>
              <span className="text-xs px-2 min-w-[50px] text-center">{Math.round(zoom * 100)}%</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={zoomIn}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom in</TooltipContent>
              </Tooltip>
            </div>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={resetView}>
                  <Move className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset view</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={toggleFullscreen}>
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Fullscreen</TooltipContent>
            </Tooltip>

            {/* Export dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting || isPngExporting}>
                  <Download className="h-4 w-4 mr-1" />
                  {isPngExporting ? 'Exporting...' : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                <DropdownMenuItem onClick={exportAsPng} disabled={isPngExporting}>
                  <Image className="w-4 h-4 mr-2" />
                  PNG Image
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportAsSvg} disabled={isPngExporting}>
                  <FileImage className="w-4 h-4 mr-2" />
                  SVG Image (scalable)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => exportToPdf('mindmap', mindMap)}>
                  <FileText className="w-4 h-4 mr-2" />
                  PDF Document
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToDocx('mindmap', mindMap)}>
                  <FileText className="w-4 h-4 mr-2" />
                  Word Document (.docx)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={exportAsMarkdown}>
                  <FileText className="w-4 h-4 mr-2" />
                  Markdown (.md)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportAsJSON}>
                  <Network className="w-4 h-4 mr-2" />
                  JSON
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={copyAsText}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy as text
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* View mode toggle */}
        <div className="flex items-center gap-2 mb-4">
          <Button
            variant={viewMode === 'radial' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('radial')}
            className="gap-1"
          >
            <Network className="h-4 w-4" />
            Visual
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="gap-1"
          >
            <List className="h-4 w-4" />
            Outline
          </Button>
        </div>

        {/* Mind map content */}
        <Card className="flex-1 overflow-hidden">
          <CardContent className="p-0">
            {viewMode === 'radial' ? (
              <RadialMindMap 
                mindMap={mindMap} 
                zoom={zoom} 
                pan={pan}
                onPan={setPan}
                selectedNode={selectedNode}
                onSelectNode={setSelectedNode}
                contentRef={mindMapContentRef}
              />
            ) : (
              <ListMindMap 
                mindMap={mindMap}
                selectedNode={selectedNode}
                onSelectNode={setSelectedNode}
              />
            )}
          </CardContent>
        </Card>

        {/* Selected node details */}
        {selectedNodeData && (
          <Card className="mt-4 bg-muted/50">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{selectedNodeData.label}</h4>
                  {selectedNodeData.description && (
                    <p className="text-sm text-muted-foreground">{selectedNodeData.description}</p>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedNode(null)}
                >
                  Clear
                </Button>
              </div>
              {selectedNodeData.children && selectedNodeData.children.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedNodeData.children.map(child => (
                    <Badge key={child.id} variant="secondary" className="text-xs">
                      {child.label}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Connections info */}
        {mindMap.connections && mindMap.connections.length > 0 && (
          <Card className="mt-4">
            <CardContent className="py-3">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Share2 className="h-4 w-4" />
                Cross-References
              </h4>
              <div className="flex flex-wrap gap-2">
                {mindMap.connections.map((conn, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="text-xs cursor-pointer hover:bg-muted"
                  >
                    {conn.from} → {conn.to}
                    {conn.label && `: ${conn.label}`}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}

export default MindMapViewer;
